import { Editor } from "@tiptap/core";
import { useTranslation } from "react-i18next";

const Toolbar = ({ editor }: { editor: Editor | null }) => {
  const { t } = useTranslation();
  if (!editor) return null;

  // --- Button class logic based on active state ---
  const buttonClass = (isActive: boolean = false) =>
    `rounded-md p-2 transition-colors ${isActive ? "bg-sidebar text-skyblue" : "text-gray-600 hover:bg-gray-100"}`;

  // --- Check if a placeholder is active anywhere in the document ---
  const isPlaceholderActive = (placeholder: string) => {
    if (!editor) return false;
    const { doc } = editor.state;
    let found = false;

    // Look through the document and find the placeholder token
    doc.descendants((node) => {
      if (node.type.name === "placeholder" && node.attrs.token === placeholder) {
        found = true;
        return false; // stop early
      }
      return true;
    });

    return found;
  };

  // --- Insert or remove placeholder based on active state ---
  const togglePlaceholder = (placeholder: string, color: string) => {
    const isActive = isPlaceholderActive(placeholder);

    if (isActive) {
      // If the placeholder is active, remove it
      editor.chain().focus().deletePlaceholder(placeholder).run(); // Uses the deletePlaceholder command
    } else {
      // Insert placeholder if it doesn't exist
      editor.chain().focus().insertPlaceholder(placeholder, color).run(); // Uses the insertPlaceholder command
    }
  };

  // --- Placeholder buttons config ---
  const placeholders = [
    { token: "{{username}}", color: "#ffe066", label:   t("toolbar.customerName"), },
    { token: "{{date}}", color: "#a5d8ff", label:   t("toolbar.orderDate"), },
    { token: "{{invoice_pdf}}", color: "#d0bfff", label:   t("toolbar.invoicePDF"), },
    { token: "{{order_number}}", color: "#b2f2bb", label:  t("toolbar.orderNumber"), },
    { token: "{{rate_link}}", color: "#b2f2bc", label:  t("toolbar.rateLink"), },
  ];

  return (
    <div className="flex flex-wrap gap-2 rounded-t-lg border-b bg-gray-50 p-2">
      {/* Formatting Buttons */}
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive("bold"))}
      >
        <strong>B</strong>
      </button>

      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive("italic"))}
      >
        <em>I</em>
      </button>

      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive("bulletList"))}
      >
        • List
      </button>

      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive("orderedList"))}
      >
        1. List
      </button>

      {/* Placeholder Buttons */}
      {placeholders.map(({ token, color, label }) => (
        <button
          key={token}
          onClick={() => togglePlaceholder(token, color)}
          className={buttonClass(isPlaceholderActive(token))}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export default Toolbar;
